import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TableHead,
} from "@mui/material";
import { IniInfo } from "system/types/interfaces";
import { useSelector } from "react-redux";
import { RootState } from "system/store";
import { DeleteRegular } from "@fluentui/react-icons";
import { useDebouncedCallback } from "use-debounce";

interface Range {
  cidr: string;
  dnsSuffix: string;
  error: string | null;
}

interface AddSiteSubnetModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (item: IniInfo) => void;
  onUpdate: (item: IniInfo) => void;
  initialItem?: IniInfo | null;
}

export const AddSiteSubnetModal: React.FC<AddSiteSubnetModalProps> = ({ open, onClose, onAdd, onUpdate, initialItem }) => {
  const [siteName, setSiteName] = useState("");
  const [ranges, setRanges] = useState<Range[]>([{ cidr: "", dnsSuffix: "", error: null }]);
  const [siteError, setSiteError] = useState<string | null>(null);
  const sites = useSelector((state: RootState) => state.policyEditor.sitesAndSubnets || []);

  const calculateMostCommonDnsSuffix = useCallback((): string => {
    const suffixes = new Map<string, number>();
    sites.forEach((site) => {
      if (site.value) {
        const parsedRanges = parseRanges(site.value);
        parsedRanges.forEach((range) => {
          const suffix = range.dnsSuffix || "";
          suffixes.set(suffix, (suffixes.get(suffix) || 0) + 1);
        });
      }
    });
    return Array.from(suffixes.entries()).reduce((a, b) => (a[1] > b[1] ? a : b))[0] || "";
  }, [sites]);

  useEffect(() => {
    if (open) {
      if (initialItem) {
        setSiteName(initialItem.name);
        setRanges(parseRanges(initialItem.value).map((r) => ({ ...r, error: null })));
        setSiteError(null); // Reset error on open
      } else {
        setSiteName("");
        setRanges([{ cidr: "", dnsSuffix: calculateMostCommonDnsSuffix(), error: null }]);
        setSiteError(null); // Reset error on open
      }
    }
  }, [open, initialItem, calculateMostCommonDnsSuffix]);

  const parseRanges = (value: string): Range[] => {
    return value.split(",").map((r) => {
      const [cidr, ...dnsParts] = r.trim().split("/").slice(1) || [""];
      return { cidr: `${r.trim().split("/")[0]}/${cidr}`, dnsSuffix: dnsParts.join("/") || "", error: null };
    });
  };

  const formatRanges = (ranges: Range[]): string => {
    return ranges.map((r) => `${r.cidr}${r.dnsSuffix ? `/${r.dnsSuffix}` : ""}`).join(", ");
  };

  const validateCidr = (cidr: string): string | null => {
    const cidrPattern = /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\/(\d{1,2})$/;
    const [ip, prefix] = cidr.split("/");
    if (!cidrPattern.test(cidr)) return "Invalid CIDR format (e.g., 192.168.1.0/24).";
    const octets = ip.split(".").map(Number);
    if (octets.some((octet) => isNaN(octet) || octet < 0 || octet > 255)) return "Invalid IP address.";
    const prefixNum = parseInt(prefix, 10);
    if (prefixNum < 0 || prefixNum > 32) return "Prefix must be between 0 and 32.";
    return null;
  };

  const sanitizeSiteName = (name: string): string => {
    if (!name) return "";
    let sanitized = name.replace(/[^a-zA-Z0-9\-_]/g, "-");
    sanitized = sanitized.replace(/^-+|-+$/g, "");
    return sanitized;
  };

  const debouncedSanitizeAndValidate = useDebouncedCallback((value: string) => {
    const san = sanitizeSiteName(value);
    setSiteName(san);
    const err = validateSiteName(san) || checkForDuplicateSiteName(san);
    setSiteError(err);
  }, 1000);

  const validateSiteName = (sanitizedName: string): string | null => {
    if (!sanitizedName) return "Site Name is required.";
    const validPattern = /^[a-zA-Z0-9][a-zA-Z0-9\-_]*[a-zA-Z0-9]$/;
    if (!validPattern.test(sanitizedName)) {
      return "Site name must begin and end with a letter or number [a-zA-Z0-9], and only allow [a-z], [A-Z], [0-9], '-', or '_'.";
    }
    return null;
  };

  const checkForDuplicateSiteName = (name: string): string | null => {
    const existingNames = sites.filter((site) => site.name !== initialItem?.name).map((site) => site.name.toLowerCase());
    if (existingNames.includes(name.toLowerCase())) {
      return "A site with this name already exists.";
    }
    return null;
  };

  const validateInput = (): boolean => {
    const siteNameError = validateSiteName(siteName) || checkForDuplicateSiteName(siteName);
    setSiteError(siteNameError);

    const rangeErrors = ranges.map((r) => ({ ...r, error: validateCidr(r.cidr) }));
    setRanges(rangeErrors);

    return !siteNameError && !rangeErrors.some((r) => r.error);
  };

  const handleAddRange = () => {
    if (ranges.length > 1) {
      setRanges([...ranges, { cidr: "", dnsSuffix: ranges[ranges.length - 1].dnsSuffix, error: null }]);
    } else {
      setRanges([...ranges, { cidr: "", dnsSuffix: calculateMostCommonDnsSuffix(), error: null }]);
    }
  };

  const handleRemoveRange = (index: number) => {
    setRanges(ranges.filter((_, i) => i !== index));
  };

  const handleUpdateRange = (index: number, field: keyof Range, value: string) => {
    const newRanges = [...ranges];
    newRanges[index][field] = value;
    newRanges[index].error = validateCidr(newRanges[index].cidr);
    setRanges(newRanges);
  };

  const handleSiteNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSiteName(value);
    debouncedSanitizeAndValidate(value);
  };

  const handleSave = () => {
    if (!validateInput()) return;

    const newItem: IniInfo = {
      id: initialItem?.id || new Date().getTime().toString(),
      name: siteName,
      value: formatRanges(ranges),
      delimiter: "=",
      pendingChanges: true,
      isDeleted: false,
    };

    if (initialItem) {
      onUpdate(newItem);
    } else {
      onAdd(newItem);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#fff" }}>Site and Subnet</DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1b1a19", display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Site Name"
          value={siteName}
          onChange={handleSiteNameChange}
          fullWidth
          sx={{ input: { color: "#fff" }, mt: 2 }}
          error={!!siteError}
          helperText={siteError}
        />
        <Typography sx={{ color: "#fff" }}>IP Ranges</Typography>
        <Table size="small" sx={{ backgroundColor: "#2a2a2a" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#fff", width: "40%" }}>CIDR</TableCell>
              <TableCell sx={{ color: "#fff", width: "40%" }}>Connection-specific DNS Suffix</TableCell>
              <TableCell sx={{ color: "#fff", width: "20%" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ranges.map((range, index) => (
              <TableRow key={index}>
                <TableCell sx={{ color: "#fff", width: "40%" }}>
                  <TextField
                    value={range.cidr}
                    onChange={(e) => handleUpdateRange(index, "cidr", e.target.value)}
                    fullWidth
                    sx={{ input: { color: "#fff" } }}
                    placeholder="e.g., 192.168.1.0/24"
                    error={!!range.error}
                    helperText={range.error}
                    InputProps={{
                      sx: { borderColor: range.error ? "#d32f2f" : undefined },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: "#fff", width: "40%" }}>
                  <TextField
                    value={range.dnsSuffix}
                    onChange={(e) => handleUpdateRange(index, "dnsSuffix", e.target.value)}
                    fullWidth
                    sx={{ input: { color: "#fff" } }}
                    placeholder="e.g., internal.meetaiden.com"
                  />
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <IconButton onClick={() => handleRemoveRange(index)} color="error" disabled={ranges.length <= 1}>
                    <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button variant="contained" color="primary" onClick={handleAddRange} sx={{ color: "#fff", width: "150px", mb: 2 }}>
          Add Range
        </Button>

        <Typography variant="caption" sx={{ color: "#aaa" }}>
          The Connection-specific DNS Suffix allows AidenBot to differentiate between organization-managed networks and external networks
          with similar IP ranges. This value is often configured as DHCP option 15. In Windows, the value for a specific device can be
          obtained by typing ‘ipconfig’ at the command prompt.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
        <Button onClick={onClose} sx={{ color: "#fff" }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" sx={{ color: "#fff" }} disabled={!!siteError || ranges.some((r) => r.error)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
